import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import Drawer from '../../components/Drawer';
import { apiError, formatDateTime, formatQty } from '../fbaTransfers/format';

const API = '/api/v1/warehouse/amazon-fba-inventory';
const TH = 'whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500';
const TD = 'whitespace-nowrap px-3 py-2 text-sm text-gray-900 tabular-nums';
const NUM = `${TD} text-right`;
const STICKY_SKU = 'sticky left-0 w-[160px] min-w-[160px] max-w-[160px] truncate';
const STICKY_AMAZON = 'sticky left-[160px] min-w-[220px] max-w-[220px] truncate shadow-[inset_-1px_0_0_#e5e7eb]';

const MAPPING_LABELS = { MAPPED: 'Mapped', AMBIGUOUS: 'Needs mapping (2+ Eadin SKUs)', UNMAPPED: 'Needs mapping (no link)' };
const STATE_STYLES = {
  FRESH: 'border-green-200 bg-green-50 text-green-800',
  STALE: 'border-amber-300 bg-amber-50 text-amber-900',
  NO_DATA: 'border-red-200 bg-red-50 text-red-800',
};

const signed = (value) => (value > 0 ? `+${formatQty(value)}` : formatQty(value));

const MappingChip = ({ status }) => (
  <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${status === 'MAPPED' ? 'bg-gray-100 text-gray-700' : 'bg-red-100 text-red-700'}`}>
    {status === 'MAPPED' ? 'Mapped' : 'Needs mapping'}
  </span>
);

const FreshnessBanner = ({ status, stranded }) => {
  if (!status) return null;
  const sync = status.current_sync;
  const attempt = status.latest_attempt;
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${STATE_STYLES[status.state] || STATE_STYLES.NO_DATA}`}>
      <div className="font-semibold">
        {status.state === 'FRESH' && 'Fresh'}
        {status.state === 'STALE' && `Stale: last good sync is older than ${status.stale_after_minutes} minutes. Numbers below are from that sync, not current.`}
        {status.state === 'NO_DATA' && 'No successful Amazon sync yet. Nothing is shown until a full pull passes every check.'}
      </div>
      <div className="mt-0.5 text-xs">
        Last good sync: {sync ? `${formatDateTime(sync.finished_at)} (#${sync.id}, ${sync.pages} pages, ${formatQty(sync.msku_count)} Amazon SKUs, ${formatQty(sync.fnsku_count)} FNSKUs)` : '—'}
        {' · '}Latest attempt: {attempt ? `${attempt.status} at ${formatDateTime(attempt.finished_at || attempt.started_at)}` : '—'}
        {' · '}Stranded report: {stranded ? `${formatDateTime(stranded.finished_at)} (${stranded.rows} rows${stranded.amazon_status === 'CANCELLED' ? ', Amazon had no stranded data' : ''})` : 'not pulled yet'}
      </div>
      {!!status.consecutive_failures && (
        <div className="mt-1 text-xs font-medium">
          {status.consecutive_failures} failed run{status.consecutive_failures === 1 ? '' : 's'} in a row. Last error: {status.last_error || 'unknown'}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, hint, tone = 'text-gray-900' }) => (
  <div className="min-w-[120px] rounded-lg border border-gray-200 px-3 py-2">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-lg font-semibold tabular-nums ${tone}`}>{value}</div>
    {hint && <div className="text-[11px] text-gray-500">{hint}</div>}
  </div>
);

const DetailTable = ({ head, rows }) => (
  <div className="overflow-auto rounded border">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50"><tr>{head.map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length ? rows.map((cells, i) => <tr key={i}>{cells.map((c, j) => <td key={j} className={TD}>{c}</td>)}</tr>)
          : <tr><td colSpan={head.length} className="px-3 py-3 text-sm text-gray-500">None</td></tr>}
      </tbody>
    </table>
  </div>
);

const FnskuDetail = ({ fnsku, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!fnsku) return;
    setData(null);
    setError(null);
    axios.get(`${API}/fnsku/${encodeURIComponent(fnsku)}`)
      .then(({ data: body }) => setData(body))
      .catch((err) => setError(apiError(err, 'Failed to load FNSKU')));
  }, [fnsku]);
  const line = data?.line;
  return (
    <Drawer isOpen={!!fnsku} onClose={onClose} title={`FNSKU ${fnsku || ''}`} size="max-w-4xl">
      <div className="space-y-4 p-4 text-sm">
        {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800">{error}</div>}
        {!data && !error && <div className="text-gray-500">Loading…</div>}
        {line && (
          <>
            <div>
              <div className="font-semibold text-gray-900">{line.product_name}</div>
              <div className="text-xs text-gray-500">ASIN {line.asin} · Amazon SKUs {line.seller_skus.join(', ')} · Amazon updated {formatDateTime(line.amazon_last_updated_at)}</div>
              <div className="mt-1">
                Eadin SKU: {line.mapping_status === 'MAPPED' ? <b>{line.supply_sku}</b> : <span className="text-red-700">{MAPPING_LABELS[line.mapping_status]}</span>}
                {line.mapping_status !== 'MAPPED' && !!line.mapping_candidates.length && (
                  <span className="text-gray-600"> · catalog links: {line.mapping_candidates.map((c) => `${c.supply_sku}${c.status && c.status !== 'active' ? ` (${c.status})` : ''}`).join(', ')}</span>
                )}
              </div>
            </div>
            <div>
              <h3 className="mb-1 font-semibold">Reconciliation</h3>
              <DetailTable
                head={['Amazon inbound', 'Eadin in transit', 'Difference', 'Needs recovery']}
                rows={[[formatQty(line.amazon_inbound), formatQty(line.eadin_in_transit), signed(line.inbound_variance), formatQty(line.needs_recovery)]]}
              />
            </div>
            <div>
              <h3 className="mb-1 font-semibold">Linked transfers</h3>
              <DetailTable
                head={['Transfer', 'Status', 'Amazon shipment', 'Amazon SKU', 'Removed', 'Amazon shipped', 'Amazon received', 'In transit', 'Needs recovery']}
                rows={data.transfers.map((t) => [
                  <a key="t" className="text-blue-700 underline" href={`/fba-transfers?transfer=${t.transfer_id}`}>{t.transfer_number}</a>,
                  t.status, t.amazon_shipment_id || '—', t.amazon_seller_sku, formatQty(t.removed),
                  formatQty(t.amazon_shipped), formatQty(t.amazon_received), formatQty(t.in_transit), formatQty(t.needs_recovery),
                ])}
              />
            </div>
            <div>
              <h3 className="mb-1 font-semibold">Breakdowns</h3>
              <DetailTable
                head={['Bucket', 'Units']}
                rows={[
                  ...Object.entries(line.unfulfillable_breakdown || {}).map(([k, v]) => [`Unfulfillable: ${k}`, formatQty(v)]),
                  ...(Array.isArray(line.researching_breakdown) ? line.researching_breakdown : []).map((r) => [`Researching: ${r.name}`, formatQty(r.quantity)]),
                ]}
              />
            </div>
            {!!data.stranded_rows.length && (
              <div>
                <h3 className="mb-1 font-semibold">Stranded</h3>
                <DetailTable head={['Amazon SKU', 'Units', 'Reason', 'Status', 'Since']} rows={data.stranded_rows.map((r) => [r.seller_sku || '—', formatQty(r.stranded_qty), r.reason || '—', r.status || '—', r.date_stranded || '—'])} />
              </div>
            )}
            <div>
              <h3 className="mb-1 font-semibold">Recent syncs</h3>
              <DetailTable
                head={['Sync', 'Finished', 'Total', 'Sellable', 'Inbound', 'Reserved', 'Unfulfillable']}
                rows={data.history.map((h) => [`#${h.sync_id}`, formatDateTime(h.finished_at), formatQty(h.total_quantity), formatQty(h.fulfillable), formatQty(h.amazon_inbound), formatQty(h.reserved_total), formatQty(h.unfulfillable_total)])}
              />
            </div>
            <div>
              <h3 className="mb-1 font-semibold">Raw Amazon rows ({(line.raw || []).length})</h3>
              <pre className="max-h-80 overflow-auto rounded bg-gray-50 p-2 text-xs">{JSON.stringify(line.raw, null, 2)}</pre>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

const AmazonFbaInventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const openFnsku = searchParams.get('fnsku');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ q: '', mapping: '', stock: '', inbound: false, variance: false });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: body } = await axios.get(API);
      setData(body);
    } catch (err) {
      setError(apiError(err, 'Failed to load Amazon FBA inventory'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setFnsku = (fnsku) => {
    const next = new URLSearchParams(searchParams);
    if (fnsku) next.set('fnsku', fnsku); else next.delete('fnsku');
    setSearchParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return (data?.lines || []).filter((row) => {
      if (q && ![row.fnsku, row.asin, row.supply_sku, row.product_name, ...row.seller_skus].some((v) => String(v || '').toLowerCase().includes(q))) return false;
      if (filters.mapping === 'MAPPED' && row.mapping_status !== 'MAPPED') return false;
      if (filters.mapping === 'NEEDS' && row.mapping_status === 'MAPPED') return false;
      if (filters.stock === 'in' && !(row.total_quantity > 0)) return false;
      if (filters.stock === 'sellable' && !(row.fulfillable > 0)) return false;
      if (filters.stock === 'zero' && row.total_quantity !== 0) return false;
      if (filters.inbound && !(row.amazon_inbound > 0 || row.eadin_in_transit > 0)) return false;
      if (filters.variance && row.inbound_variance === 0) return false;
      return true;
    }).sort((a, b) => b.total_quantity - a.total_quantity || a.fnsku.localeCompare(b.fnsku));
  }, [data, filters]);

  const totals = data?.totals;
  const stale = data?.status?.state !== 'FRESH';

  return (
    <div className="flex h-[calc(100vh-48px)] min-h-[680px] flex-col overflow-hidden bg-white">
      <header className="shrink-0 border-b border-gray-200 px-4 py-2.5">
        <h1 className="text-base font-semibold text-gray-900">Amazon FBA Inventory</h1>
        <p className="text-xs text-gray-500">Read-only mirror of Amazon&apos;s FBA inventory, one row per FNSKU. Amazon SKUs that share an FNSKU are the same units and are counted once.</p>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
        <FreshnessBanner status={data?.status} stranded={data?.stranded} />
        {totals && (
          <div className={`flex flex-wrap gap-2 ${stale ? 'opacity-70' : ''}`}>
            <Stat label="Physical FBA total" value={formatQty(totals.total_quantity)} hint={`${formatQty(totals.fnskus)} FNSKUs from ${formatQty(totals.msku_rows)} Amazon SKUs`} />
            <Stat label="Sellable" value={formatQty(totals.fulfillable)} />
            <Stat label="Reserved" value={formatQty(totals.reserved_total)} />
            <Stat label="Amazon inbound" value={formatQty(totals.amazon_inbound)} />
            <Stat label="Eadin in transit" value={formatQty(totals.eadin_in_transit)} hint={`Difference ${signed(totals.amazon_inbound - totals.eadin_in_transit)}`} />
            <Stat label="Unfulfillable" value={formatQty(totals.unfulfillable_total)} />
            <Stat label="Researching" value={formatQty(totals.researching_total)} hint="Not in Amazon total" />
            <Stat label="Stranded" value={formatQty(totals.stranded)} hint="Daily report" />
            <Stat label="Needs mapping" value={`${formatQty(totals.needs_mapping_with_stock)} FNSKUs`} hint={`${formatQty(totals.needs_mapping_units)} units not assigned`} tone={totals.needs_mapping_with_stock ? 'text-red-700' : 'text-gray-900'} />
            {!!totals.needs_recovery && <Stat label="Needs recovery" value={formatQty(totals.needs_recovery)} tone="text-red-700" />}
          </div>
        )}
        {!!data?.mapping_exceptions?.length && (
          <details className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm">
            <summary className="cursor-pointer font-medium text-red-800">
              {data.mapping_exceptions.length} FNSKUs with stock need a catalog fix before Eadin assigns their units
            </summary>
            <div className="mt-2 overflow-auto rounded border border-red-100 bg-white">
              <table className="min-w-full">
                <thead className="bg-gray-50"><tr>{['FNSKU', 'Amazon SKUs', 'Problem', 'Catalog links found', 'Units', 'Sellable'].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {data.mapping_exceptions.map((row) => (
                    <tr key={row.fnsku} className="cursor-pointer hover:bg-gray-50" onClick={() => setFnsku(row.fnsku)}>
                      <td className={TD}>{row.fnsku}</td>
                      <td className={TD}>{row.seller_skus.join(', ')}</td>
                      <td className={TD}>{MAPPING_LABELS[row.mapping_status]}</td>
                      <td className={TD}>{row.candidates.map((c) => c.supply_sku).join(' / ') || '—'}</td>
                      <td className={NUM}>{formatQty(row.total_quantity)}</td>
                      <td className={NUM}>{formatQty(row.fulfillable)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Eadin SKU, Amazon SKU, ASIN or FNSKU" className="w-72 rounded-md border border-gray-300 px-2 py-1.5" />
          <select value={filters.mapping} onChange={(e) => setFilters((f) => ({ ...f, mapping: e.target.value }))} className="rounded-md border border-gray-300 px-2 py-1.5">
            <option value="">All mappings</option>
            <option value="MAPPED">Mapped</option>
            <option value="NEEDS">Needs mapping</option>
          </select>
          <select value={filters.stock} onChange={(e) => setFilters((f) => ({ ...f, stock: e.target.value }))} className="rounded-md border border-gray-300 px-2 py-1.5">
            <option value="">All stock</option>
            <option value="in">Any units at Amazon</option>
            <option value="sellable">Sellable &gt; 0</option>
            <option value="zero">Zero units</option>
          </select>
          <label className="flex items-center gap-1"><input type="checkbox" checked={filters.inbound} onChange={(e) => setFilters((f) => ({ ...f, inbound: e.target.checked }))} /> Inbound only</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={filters.variance} onChange={(e) => setFilters((f) => ({ ...f, variance: e.target.checked }))} /> Inbound difference only</label>
          <span className="text-gray-500">{formatQty(rows.length)} rows</span>
        </div>
        <div className="min-h-0 flex-1 overflow-auto rounded-lg border">
          <table className="min-w-full">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className={`${TH} ${STICKY_SKU} top-0 z-30 bg-gray-50`}>Eadin SKU</th>
                <th className={`${TH} ${STICKY_AMAZON} top-0 z-30 bg-gray-50`}>Amazon SKUs</th>
                {['ASIN / FNSKU', 'Mapping'].map((h) => <th key={h} className={TH}>{h}</th>)}
                {['Sellable', 'Inbound working', 'Inbound shipped', 'Inbound receiving', 'Reserved', 'Unfulfillable', 'Researching', 'Stranded', 'Future supply', 'Amazon total', 'Eadin in transit', 'Inbound diff.'].map((h) => <th key={h} className={`${TH} text-right`}>{h}</th>)}
                <th className={TH}>Amazon updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={17} className="px-4 py-6 text-sm text-gray-500">Loading…</td></tr>}
              {!loading && !rows.length && <tr><td colSpan={17} className="px-4 py-6 text-sm text-gray-500">{data?.lines?.length ? 'No rows match these filters.' : 'No Amazon inventory loaded yet.'}</td></tr>}
              {!loading && rows.map((row) => (
                <tr key={row.fnsku} onClick={() => setFnsku(row.fnsku)} className="group cursor-pointer hover:bg-gray-50">
                  <td className={`${TD} ${STICKY_SKU} z-[1] bg-white group-hover:bg-gray-50`}>{row.mapping_status === 'MAPPED' ? <span className="font-medium">{row.supply_sku}</span> : <span className="text-red-700">—</span>}</td>
                  <td className={`${TD} ${STICKY_AMAZON} z-[1] bg-white group-hover:bg-gray-50`} title={row.seller_skus.join(', ')}>{row.seller_skus.join(', ')}</td>
                  <td className={TD}><div>{row.asin}</div><div className="text-xs text-gray-500">{row.fnsku}</div></td>
                  <td className={TD}><MappingChip status={row.mapping_status} /></td>
                  <td className={`${NUM} font-medium`}>{formatQty(row.fulfillable)}</td>
                  <td className={NUM}>{formatQty(row.inbound_working)}</td>
                  <td className={NUM}>{formatQty(row.inbound_shipped)}</td>
                  <td className={NUM}>{formatQty(row.inbound_receiving)}</td>
                  <td className={NUM} title={`Customer orders ${row.reserved_customer_orders} · Moving between Amazon sites ${row.reserved_fc_transfer} · Warehouse processing ${row.reserved_fc_processing}`}>{formatQty(row.reserved_total)}</td>
                  <td className={NUM}>{formatQty(row.unfulfillable_total)}</td>
                  <td className={NUM}>{formatQty(row.researching_total)}</td>
                  <td className={NUM}>{formatQty(row.stranded)}</td>
                  <td className={NUM}>{formatQty(row.future_supply_reserved + row.future_supply_buyable)}</td>
                  <td className={`${NUM} font-semibold`}>{formatQty(row.total_quantity)}</td>
                  <td className={NUM}>{formatQty(row.eadin_in_transit)}</td>
                  <td className={`${NUM} ${row.inbound_variance ? 'font-medium text-amber-700' : ''}`}>{signed(row.inbound_variance)}</td>
                  <td className={`${TD} text-gray-500`}>{formatDateTime(row.amazon_last_updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <FnskuDetail fnsku={openFnsku} onClose={() => setFnsku(null)} />
    </div>
  );
};

export default AmazonFbaInventory;
